/**
 * ExitIntentChecklistTrigger
 *
 * Cohort 1 exit-intent variant — fires the Buyer's Checklist modal when an
 * engaged anonymous visitor aims their cursor at the tab bar.
 *
 * Coexists with the existing `ExitIntentSampleModal` via a per-session A/B
 * coin flip stored in `sessionStorage["bl_exit_intent_variant"]`:
 *
 *   - On first arm, both components race to set the variant to "sample" or
 *     "checklist" (50/50 weighted random).
 *   - Only the component matching the chosen variant fires; the other
 *     short-circuits. They share the `bl_exit_intent_shown_v1` flag too, so
 *     at most ONE exit-intent overlay shows per session.
 *
 * Same engagement gating as Sample modal:
 *   - Desktop only (no reliable mouse-leave semantics on mobile)
 *   - 40 % scroll engagement before arming
 *   - Top-edge cursor-leave only
 *   - 90-day dismissal memory in localStorage
 *
 * Telemetry (consent-gated):
 *   - exit_intent_modal_shown    { source: "home", variant: "checklist" }
 *   - exit_intent_modal_dismissed{ source: "home", variant: "checklist", reason }
 *   - exit_intent_modal_cta_clicked already fires from BuyerChecklistModal
 *     via lead_magnet_modal_submitted.
 */
import { useEffect, useRef, useState } from "react";
import BuyerChecklistModal from "@/components/home/BuyerChecklistModal";
import { track } from "@/lib/analytics";
import { useAuth } from "@/lib/auth";

const SESSION_FLAG = "bl_exit_intent_shown_v1";
const VARIANT_KEY = "bl_exit_intent_variant";
const DISMISSED_AT_KEY = "bl_exit_intent_checklist_dismissed_at_v1";
const DISMISSED_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const VARIANT_NAME = "checklist";

function isMobileUA() {
    if (typeof navigator === "undefined") return false;
    return /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
    );
}

function recentlyDismissed() {
    try {
        const raw = localStorage.getItem(DISMISSED_AT_KEY);
        if (!raw) return false;
        const ts = Number(raw);
        if (!Number.isFinite(ts)) return false;
        return Date.now() - ts < DISMISSED_TTL_MS;
    } catch {
        return false;
    }
}

/** Lazily assign or read the per-session A/B variant. 50/50 split. */
function getOrAssignVariant() {
    try {
        const existing = sessionStorage.getItem(VARIANT_KEY);
        if (existing === "sample" || existing === "checklist") return existing;
        const v = Math.random() < 0.5 ? "sample" : "checklist";
        sessionStorage.setItem(VARIANT_KEY, v);
        return v;
    } catch {
        return "sample";
    }
}

export default function ExitIntentChecklistTrigger({ source = "home" }) {
    const [open, setOpen] = useState(false);
    const armedRef = useRef(false);
    const shownRef = useRef(false);
    const { user } = useAuth();

    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        if (user) return undefined;
        if (isMobileUA()) return undefined;
        if (getOrAssignVariant() !== VARIANT_NAME) return undefined;
        try {
            if (sessionStorage.getItem(SESSION_FLAG) === "1") return undefined;
        } catch { /* non-critical */ }
        if (recentlyDismissed()) return undefined;

        const handleScroll = () => {
            const doc = document.documentElement;
            const max = doc.scrollHeight - doc.clientHeight;
            if (max <= 0) return;
            const pct = window.scrollY / max;
            if (pct >= 0.4) armedRef.current = true;
        };

        const handleMouseLeave = (e) => {
            if (e.clientY > 0) return;
            if (!armedRef.current) return;
            if (shownRef.current) return;
            shownRef.current = true;
            try {
                sessionStorage.setItem(SESSION_FLAG, "1");
            } catch { /* non-critical */ }
            track("exit_intent_modal_shown", {
                source,
                variant: VARIANT_NAME,
            });
            setOpen(true);
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        document.addEventListener("mouseleave", handleMouseLeave);
        handleScroll();

        return () => {
            window.removeEventListener("scroll", handleScroll);
            document.removeEventListener("mouseleave", handleMouseLeave);
        };
    }, [source, user]);

    const handleOpenChange = (nextOpen) => {
        if (!nextOpen && open) {
            // Modal closed. Persist 90-day dismissal memory and emit telemetry.
            try {
                localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
            } catch { /* non-critical */ }
            track("exit_intent_modal_dismissed", {
                source,
                variant: VARIANT_NAME,
                reason: "backdrop_or_close",
            });
        }
        setOpen(nextOpen);
    };

    return (
        <BuyerChecklistModal
            open={open}
            onOpenChange={handleOpenChange}
            source="exit_intent"
        />
    );
}
